// frontend/src/hooks/useSupplierCatalogItemMutations.js
//
// Write paths for the item↔supplier relation, on both screens that edit it:
// the Supplier catalog (one supplier, many items — the only place per-relation
// TERMS are edited in bulk) and the per-item Suppliers dialog (one item, its
// suppliers — components/supplier/ItemSuppliersDialog.jsx). All five routes go
// through /api/supplier-catalog-items/:
//
//   - bulk-update  — the catalog grid's Save (dirty cells → {items:[{id, fields}]}).
//   - bulk-create  — the catalog's Add items picker (selection rule + typed costs).
//   - create       — the item dialog's Assign supplier (one relation, with a cost).
//   - set-default  — make a relation the item's default (demote-before-promote):
//                    the catalog grid's Save, one per ticked Default box, and
//                    the item dialog's Make default.
//   - delete       — remove a relation.
//
// Every write changes the item's supplier relations and, through the default
// relation, the item's cost of record (Model B), so each names both kinds. The
// ERP write gate (409 / 502) surfaces through the standard ApiError path.

import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { useApiMutation } from '../queries/useApiMutation';

const CHANGES = [DATA.supplierLinks, DATA.items];


/**
 * Bulk-update supplier→item relations.
 * Variables: `{ items: [{ id, fields }, ...] }`.
 * Response: `{ results: [{id, success, error?}], summary: {total, succeeded, failed} }`.
 *
 * A cost typed on an item's DEFAULT supplier row also moves that item's cost
 * of record and sends it to the sales channel (Model B), with no confirmation:
 * the typed price is the answer. A row a connector rejects fails alone.
 */
export function useSupplierCatalogItemBulkUpdate() {
  return useApiMutation({
    mutationFn: ({ items }) => apiJson.post('/api/supplier-catalog-items/bulk-update/', { items }),
    changes: CHANGES,
    // Item setup saves relation fields too; its rows show the default's.
    edits: ['/api/supplier-catalog-items/', '/api/item-locations/'],
  });
}

/**
 * Add every item an item-picker selection names to one supplier.
 * Variables: `{ query, body }`.
 *
 * `query` is the picker grid's own filter string (`?search=…&status=active&
 * exclude_supplier=…`), sent verbatim so a header-checkbox select-all resolves
 * server-side against exactly the set the merchant was looking at — the rule
 * names a filter, not a list, so the payload is the same size for 5 items or
 * 500. `body` carries `{supplier_id, ids | select_all + exclude_ids, costs}`.
 *
 * NO confirmation branch, and that is the endpoint's contract rather than an
 * omission here. A row moves the
 * item's cost of record — and mirrors it to the sales channel — only when it is
 * both PRICED and the item's first relation, i.e. the merchant typed what this
 * supplier charges for an item that now sources from it. The confirmation the
 * single create raises asks "keep the existing cost or take the new supplier's
 * price?", and a typed price has already answered it. Every other row (unpriced,
 * or on an item that already sources elsewhere) writes the relation and leaves
 * the valuation alone.
 *
 * Response: `{created, skipped_existing, rejected}` — `rejected` counts the
 * rows whose cost Shopify refused; each row is pushed before it is written,
 * so those rows were not assigned.
 */
export function useBulkCreateSupplierCatalogItems() {
  return useApiMutation({
    mutationFn: ({ query, body }) => apiJson.post(
      `/api/supplier-catalog-items/bulk-create/${query || ''}`, body,
    ),
    changes: CHANGES,
  });
}

/**
 * Add ONE supplier to an item, with the terms the merchant typed.
 * Variables: `{ itemId, supplierId, fields?, confirm? }` — `fields` is a
 * subset of the relation's commercial terms (`cost`, `supplier_sku`,
 * `lead_time_days`, …), `confirm` an opaque gate answer.
 *
 * Model B: when this relation becomes the item's default — always, for the
 * item's FIRST supplier — the item's cost of record follows it. If that moves
 * a cost the item already holds, the endpoint replies
 * `{ requires_confirmation: true, … }` and writes NOTHING; the caller shows
 * the shared modal and retries with the fragment it built (the same contract
 * as `useSetDefaultSupplierCatalogItem` below). A supplier added to an item
 * that already sources elsewhere is a plain non-default relation and never
 * gates.
 *
 * Response: the created relation row (201), or the gate body (200).
 */
export function useCreateSupplierCatalogItem() {
  return useApiMutation({
    mutationFn: ({ itemId, supplierId, fields, confirm }) => apiJson.post(
      '/api/supplier-catalog-items/',
      { item_id: itemId, supplier_id: supplierId, ...(fields || {}), ...(confirm || {}) },
    ),
    changes: CHANGES,
  });
}

/**
 * Make a relation the item's default. Variables: `{ id, confirm? }`.
 *
 * Model B: the election re-pegs the item's cost of record to this supplier. When
 * that moves the cost the endpoint replies `{ requires_confirmation: true, … }`
 * (no write); the caller retries with the confirmation fragment the modal built.
 *
 * The fragment is passed through OPAQUE rather than rebuilt here: the modal
 * owns which question is being asked and which token answers it
 * (utils/writeGates.js), and a hook that rebuilt the token would be a second
 * copy of that rule.
 */
export function useSetDefaultSupplierCatalogItem() {
  return useApiMutation({
    mutationFn: ({ id, confirm }) => apiJson.post(
      `/api/supplier-catalog-items/${id}/set-default/`, confirm || {},
    ),
    changes: CHANGES,
    // The flag moves between the item's rows and its cost follows; no row is
    // added or removed, on either grid that shows them.
    edits: ['/api/supplier-catalog-items/', '/api/item-locations/'],
  });
}

/**
 * Delete a relation. Variables: `{ id }`.
 * Response: `{ promoted_default: {supplier_id, supplier_name, item_code} | null }`
 * — the supplier auto-promoted to default when the deleted relation was it.
 */
export function useDeleteSupplierCatalogItem() {
  return useApiMutation({
    mutationFn: ({ id }) => apiJson.delete(`/api/supplier-catalog-items/${id}/`),
    changes: CHANGES,
  });
}
